import React, { useMemo, useState, useCallback } from 'react';
import { AskRequest } from '@hone-automation/common';
import { useNavigateToGroup } from 'hooks/queue';
import { prefixClass } from 'lib/utils';
import { IconFolder, IconReopen } from 'components/Icons';
import { Tooltip } from 'presentation/components/KSQ2/Common';
import { RequestIdentifier } from './components/RequestIdentifier';
import { AssignmentButton } from './components/AssignmentButton';
import { DueDateButton } from './components/DueDateButton';
import { ActionsDropdown } from './components/ActionsDropdown';
import { HeaderControls } from './components/HeaderControls';
import { getRequestActionVisibility } from '../../../../utils/actionVisibility';
import { EditableTitle } from './components/EditableTitle';
import { useGroups } from '../../../../../Dashboard/useGroups';
import { useAddToGroup, useRemoveFromGroup, useCreateGroup, useDissolveGroup } from 'hooks/queue';
import { useQueueContext } from '../../../../../Queue/QueueContext';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { AskRequestTypes } from '../../../../../../../constants';
import { CreateGroupModal } from '../../../../../AskRequest/CreateGroupModal';
import './Header.scss';

interface HeaderProps {
  request: AskRequest;
  secondPhase?: boolean;
  canManageQueue?: boolean;
  /** Optional amount to display (used in DataRowModal context) */
  amount?: string;
  /** Whether to show the status label (For Me, For Client, Resolved) */
  showStatusLabel?: boolean;
}

/**
 * ConversationPanel Header
 *
 * Displays request information and action buttons.
 * Uses a gradient overlay approach for the blocker state.
 */
export const Header: React.FC<HeaderProps> = ({
  request,
  secondPhase,
  canManageQueue,
  amount,
  showStatusLabel = false,
}) => {
  const prefix = prefixClass('conversation-header');
  const isBlocker = request.priority === 'Blocker';
  const isGroup = request.type === AskRequestTypes.Group;
  const { showDueDate } = getRequestActionVisibility(request, { canManageQueue });
  const isInGroup = !!request.parentId;

  // Use shared hooks for groups (same source as BrowserPanel)
  const groups = useGroups();
  const { state, dispatch, queryData } = useQueueContext();
  const { navigateToGroup } = useNavigateToGroup();

  // Group operations (add, remove, dissolve, create)
  const currentLocationId = useLocationsStore(state => state.currentLocationId) || '';
  const { addToGroup } = useAddToGroup(currentLocationId);
  const { removeFromGroup } = useRemoveFromGroup(currentLocationId);
  const { createGroup } = useCreateGroup(currentLocationId);
  const { dissolveGroup } = useDissolveGroup(currentLocationId);

  const handleAddToGroup = useCallback((groupId: string) => addToGroup(request.id, groupId), [addToGroup, request.id]);

  const handleRemoveFromGroup = useCallback(
    () => removeFromGroup(request.id),
    [removeFromGroup, request.id, request.parentId]
  );

  const handleDissolveGroup = useCallback(
    async (requestCount?: number) => {
      const dissolved = await dissolveGroup(request.id, request.type, requestCount);
      if (dissolved) dispatch({ type: 'SET_SELECTED_ITEM', payload: null });
    },
    [dissolveGroup, request.id, request.type, dispatch]
  );

  // Create group modal state
  const [groupModalOpen, setGroupModalOpen] = useState(false);

  const handleGroupSubmit = useCallback(
    (data: { title: string }) => {
      createGroup({ title: data.title, comment: 'Group created', childRequestIds: [request.id] });
      setGroupModalOpen(false);
    },
    [createGroup, request.id]
  );

  // Determine if request is in a group (has parentId)
  const currentGroupId = request.parentId;

  // Get the current group name for tooltip
  const currentGroupName = currentGroupId ? groups.find(g => g.id === currentGroupId)?.name : undefined;

  // Child count for group headers
  const groupChildInfo = useMemo(() => {
    if (!isGroup) return undefined;
    const viewChildren = queryData.queryRequests.filter(r => r.parentId === request.id && r.type !== 'Group');
    const groupSummary = groups.find(g => g.id === request.id);
    const trueTotal = groupSummary?.requestCount ?? viewChildren.length;
    const activeView = state.activeView;
    const viewLabel = activeView && activeView !== 'Groups' ? activeView : undefined;
    return {
      viewCount: viewChildren.length,
      trueTotal,
      viewLabel,
    };
  }, [isGroup, queryData.queryRequests, request.id, state.activeView, groups]);

  return (
    <div className={prefix('container')}>
      {/* Gradient background for the wrapper (skip blocker styling for groups) */}
      <div
        className={`${prefix('wrapper')} ${isBlocker && !isGroup ? prefix('wrapper--blocker') : ''} ${isGroup ? prefix('wrapper--group') : ''} ${isGroup && state.activeGroupId === request.id ? prefix('wrapper--active-group') : ''}`}
      >
        {/* Content layer */}
        <div className={prefix('content')}>
          {/* Left side */}
          <div className={prefix('left')}>
            <div className={prefix('left-title')}>
              {/* Group member indicator - small amber circle with folder icon */}
              {!isGroup && isInGroup && (
                <Tooltip
                  content={
                    <div>
                      <div>{currentGroupName || 'In a group'}</div>
                      <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>Click to open group</div>
                    </div>
                  }
                >
                  <div
                    className={prefix('group-member-icon')}
                    onClick={() => {
                      if (currentGroupId) {
                        navigateToGroup(currentGroupId);
                      }
                    }}
                  >
                    <IconFolder />
                  </div>
                </Tooltip>
              )}
              <EditableTitle request={request} canEdit={canManageQueue} />
            </div>
            <div className={prefix('left-content')}>
              <RequestIdentifier request={request} canManageQueue={canManageQueue} />
              {/* Child count for groups */}
              {isGroup && groupChildInfo && state.activeGroupId !== request.id && (
                <Tooltip content="View group items" showDelay={600}>
                  <span
                    className={prefix('group-child-count')}
                    onClick={() => {
                      dispatch({ type: 'SET_ACTIVE_GROUP_ID', payload: request.id });
                      dispatch({ type: 'SET_ACTIVE_VIEW', payload: 'Groups' });
                    }}
                  >
                    {groupChildInfo.viewCount === groupChildInfo.trueTotal
                      ? `${groupChildInfo.trueTotal} ${groupChildInfo.trueTotal === 1 ? 'request' : 'requests'}`
                      : `${groupChildInfo.viewCount} of ${groupChildInfo.trueTotal} requests`}
                    {groupChildInfo.viewLabel ? ` on '${groupChildInfo.viewLabel}'` : ''}
                    <IconReopen />
                  </span>
                </Tooltip>
              )}
              {/* Hide AssignmentButton and DueDateButton for groups */}
              {!isGroup && <AssignmentButton request={request} canManageQueue={canManageQueue} />}
              {!isGroup && showDueDate && <DueDateButton request={request} />}
              <ActionsDropdown
                request={request}
                noBorder={!secondPhase}
                groups={groups}
                currentGroupId={currentGroupId}
                onAddToGroup={handleAddToGroup}
                onRemoveFromGroup={handleRemoveFromGroup}
                isGroup={isGroup}
                onDissolveGroup={() => handleDissolveGroup(groupChildInfo?.trueTotal)}
                onCreateGroup={() => setGroupModalOpen(true)}
              />
            </div>
          </div>

          {/* Right side */}
          <div className={prefix('right')}>
            <HeaderControls
              request={request}
              canManageQueue={canManageQueue}
              amount={amount}
              showStatusLabel={showStatusLabel}
              isGroup={isGroup}
            />
          </div>
        </div>
      </div>
      <CreateGroupModal isOpen={groupModalOpen} onClose={() => setGroupModalOpen(false)} onSubmit={handleGroupSubmit} />
    </div>
  );
};

export default Header;
